import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Clock, Eye, Pin, Lock, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubforumPage() {
  const { subforumId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subforum, setSubforum] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [newThread, setNewThread] = useState({ title: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSubforum = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/forum/subforum/${subforumId}?page=${page}`);
        if (response.ok) {
          const data = await response.json();
          setSubforum(data.subforum);
          setThreads(data.threads);
          setTotalPages(data.pages);
        }
      } catch (error) {
        console.error('Failed to fetch subforum:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubforum();
  }, [subforumId, page]);

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Login to create threads');
      return;
    }

    if (!newThread.title.trim() || !newThread.content.trim()) {
      toast.error('Title and content required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/forum/thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subforum_id: subforumId,
          title: newThread.title,
          content: newThread.content
        })
      });

      if (response.ok) {
        const thread = await response.json();
        toast.success('Thread created!');
        setNewThreadOpen(false);
        setNewThread({ title: '', content: '' });
        navigate(`/bbs/thread/${thread.thread_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create thread');
      }
    } catch (error) {
      toast.error('Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="subforum-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#666]">
        <Link to="/bbs" className="hover:text-[#ffcc00]">BBS</Link>
        <ChevronRight size={12} />
        <span className="text-[#999]">{subforum?.name || 'Loading...'}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <MessageSquare size={32} className="text-[#cc0000]" />
          <div>
            <h1 className="text-xl font-black text-white">{subforum?.name}</h1>
            <p className="text-xs text-[#666]">{subforum?.description}</p>
          </div>
        </div>

        {/* New Thread button */}
        <Dialog open={newThreadOpen} onOpenChange={(open) => {
          if (open && !user) {
            navigate('/login', { state: { from: { pathname: `/bbs/${subforumId}` } } });
            return;
          }
          setNewThreadOpen(open);
        }}>
          <DialogTrigger asChild>
            <button 
              className="btn-primary flex items-center gap-2 text-xs"
              data-testid="new-thread-btn"
            >
              <Plus size={14} />
              NEW THREAD
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#333] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Thread</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateThread} className="space-y-4">
              <Input
                placeholder="Thread title..."
                value={newThread.title}
                onChange={(e) => setNewThread(prev => ({ ...prev, title: e.target.value }))}
                className="bg-black border-[#333] text-white"
                data-testid="new-thread-title"
              />
              <Textarea
                placeholder="Your message..."
                value={newThread.content}
                onChange={(e) => setNewThread(prev => ({ ...prev, content: e.target.value }))}
                className="bg-black border-[#333] text-white min-h-[150px]"
                data-testid="new-thread-content"
              />
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full btn-primary"
                data-testid="new-thread-submit"
              >
                {submitting ? 'POSTING...' : 'POST THREAD'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Thread list */}
      <div className="border border-[#333] bg-[#111]">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-12 gap-4 p-3 bg-[#0a0a0a] border-b border-[#333] text-xs text-[#666] uppercase">
          <div className="col-span-6">Topic</div>
          <div className="col-span-2 text-center">Replies</div>
          <div className="col-span-2 text-center">Views</div>
          <div className="col-span-2">Last Post</div>
        </div>

        {/* Threads */}
        {loading ? (
          Array(5).fill(0).map((_, i) => (
            <div key={i} className="p-4 border-b border-[#222] animate-pulse">
              <div className="h-4 w-3/4 bg-[#1a1a1a] mb-2" />
              <div className="h-3 w-1/4 bg-[#1a1a1a]" />
            </div>
          ))
        ) : threads.length > 0 ? (
          threads.map((thread) => (
            <Link
              key={thread.thread_id}
              to={`/bbs/thread/${thread.thread_id}`}
              className={`ws-thread-row grid grid-cols-12 gap-4 items-center ${thread.is_sticky ? 'sticky' : ''}`}
              data-testid={`thread-${thread.thread_id}`}
            >
              {/* Topic */}
              <div className="col-span-12 md:col-span-6">
                <div className="flex items-center gap-2">
                  {thread.is_sticky && <Pin size={12} className="text-[#ffcc00]" />}
                  {thread.is_locked && <Lock size={12} className="text-[#666]" />}
                  <span className="font-bold text-white hover:text-[#ffcc00] transition-colors">
                    {thread.title}
                  </span>
                </div>
                <div className="text-xs text-[#666] mt-1">
                  by <span className="text-[#ffcc00]">{thread.author_name}</span>
                  <span className="md:hidden ml-2">• {thread.reply_count} replies</span>
                </div>
              </div>

              {/* Replies */}
              <div className="hidden md:block col-span-2 text-center">
                <span className="mono text-[#999]">{thread.reply_count}</span>
              </div>

              {/* Views */}
              <div className="hidden md:block col-span-2 text-center">
                <span className="mono text-[#999]">{thread.view_count}</span>
              </div>

              {/* Last post */}
              <div className="hidden md:block col-span-2 text-xs">
                <div className="text-[#666] flex items-center gap-1">
                  <Clock size={10} />
                  {formatDate(thread.last_reply_date)}
                </div>
                <div className="text-[#666] truncate">
                  by <span className="text-[#999]">{thread.last_reply_author}</span>
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="p-8 text-center text-[#666]">
            <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
            <p>No threads yet. Be the first to post!</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary text-xs disabled:opacity-50"
          >
            PREV
          </button>
          <span className="text-xs text-[#666] mono px-4">
            PAGE {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary text-xs disabled:opacity-50"
          >
            NEXT
          </button>
        </div>
      )}
    </div>
  );
}
